/**
 * Chat pending input store — shared state for cross-module intent passing.
 *
 * Usage:
 * - Metadata Explorer, Salesforce Release, Security Scanner, Release Scanner:
 *   call setMultiAgentIntent(prompt) then navigate to /weave (multi-agent)
 * - MultiAgentRoute: reads and clears multiAgentIntent on mount
 * - Dev-Agent (useConsumeImproveWithAIIntent): reads and clears improveWithAIIntent
 */

import { create } from "zustand";
import { registerOrgScopedReset } from "@/lib/org-scope";
import type {
  PendingFileInput,
  PendingAgentInput,
  PendingWorkflowInput,
} from "@/components/organisms/chat/types";

interface ChatNavigationPayload {
  pendingFile?: { id: string | number; name: string };
  initialMessage?: string;
  metadataType?: string;
}

export interface ChatPendingInputState {
  pendingFile: PendingFileInput | null;
  pendingAgent: PendingAgentInput | null;
  pendingWorkflow: PendingWorkflowInput | null;
  pendingMessage: string | null;

  /** In-memory "Improve with AI" intent: set before navigating to /dev-agent, consumed once on mount. No storage. */
  improveWithAIIntent: ChatNavigationPayload | null;

  /** In-memory intent to seed the MULTI-AGENT composer (e.g. "Improve with AI"
   *  from the Release scanner); set before navigating to /multi-agent, consumed
   *  once on mount. No storage. */
  multiAgentIntent: string | null;

  setPendingFile: (file: PendingFileInput | null) => void;
  setPendingAgent: (agent: PendingAgentInput | null) => void;
  setPendingWorkflow: (workflow: PendingWorkflowInput | null) => void;
  setPendingMessage: (message: string | null) => void;
  setImproveWithAIIntent: (payload: ChatNavigationPayload | null) => void;
  setMultiAgentIntent: (intent: string | null) => void;

  /** Clear file, agent, and workflow (e.g. after send). Does not clear pendingMessage. */
  clearPendingInput: () => void;
}

export const useChatPendingInputStore = create<ChatPendingInputState>()((set) => ({
  pendingFile: null,
  pendingAgent: null,
  pendingWorkflow: null,
  pendingMessage: null,
  improveWithAIIntent: null,
  multiAgentIntent: null,

  setPendingFile: (file) => set({ pendingFile: file }),
  setPendingAgent: (agent) => set({ pendingAgent: agent }),
  setPendingWorkflow: (workflow) => set({ pendingWorkflow: workflow }),
  setPendingMessage: (message) => set({ pendingMessage: message }),
  setImproveWithAIIntent: (payload) => set({ improveWithAIIntent: payload }),
  setMultiAgentIntent: (multiAgentIntent) => set({ multiAgentIntent }),

  clearPendingInput: () =>
    set({
      pendingFile: null,
      pendingAgent: null,
      pendingWorkflow: null,
    }),
}));

// ── Organization scoping ─────────────────────────────────────────────────────
//
// Every field here is a hand-off between two screens IN one organization: a file
// to attach, an agent to run, a prompt seeded from a finding. Carrying any of it
// across a switch would open a composer in Org B pre-filled with an Org A record
// that Org B cannot resolve.
registerOrgScopedReset("chatPendingInputStore", () => {
  useChatPendingInputStore.setState({
    pendingFile: null,
    pendingAgent: null,
    pendingWorkflow: null,
    pendingMessage: null,
    improveWithAIIntent: null,
    multiAgentIntent: null,
  });
});
